// usePersonalTasks — the signed-in person's personal task list, and the lists
// of anyone they assist (public.personal_access). Reads and writes go straight
// to public.personal_tasks under RLS: only an owner and their assistants can
// see or change a row. Every write checks the returned row, so a click that
// changed nothing never reads as success.
//
// Degrades quietly before migration 20261020_personal_tasks.sql: `available`
// is false and the Personal tab says the list isn't set up yet.

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { personalTaskRow, sortPersonalTasks } from '../lib/personalTasks'

const COLS = 'id, owner_id, title, notes, due_date, status, completed_at, source, created_at'

export async function addPersonalTask(input) {
  const { row, error } = personalTaskRow(input)
  if (error) return { ok: false, error }
  const { data, error: e } = await supabase.from('personal_tasks').insert(row).select(COLS)
  if (e) return { ok: false, error: e.message }
  if (!data?.length) return { ok: false, error: 'The task was not saved.' }
  return { ok: true, task: data[0] }
}

export function usePersonalTasks(userId) {
  const [access, setAccess] = useState([])
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [available, setAvailable] = useState(true)

  const load = useCallback(async () => {
    if (!userId) { setTasks([]); setLoading(false); return }
    const [{ data: acc, error: aErr }, { data, error }] = await Promise.all([
      supabase.from('personal_access').select('owner_id, reader_id'),
      supabase.from('personal_tasks').select(COLS).order('created_at', { ascending: false }).limit(500),
    ])
    if (error || aErr) {
      setAvailable(false); setTasks([]); setAccess([])
      console.info('[personal-tasks] not available yet:', (error || aErr).message)
    } else {
      setAvailable(true); setAccess(acc || []); setTasks(sortPersonalTasks(data))
    }
    setLoading(false)
  }, [userId])

  useEffect(() => { Promise.resolve().then(load) }, [load])

  const add = useCallback(async (input) => {
    const res = await addPersonalTask({ ...input, createdBy: userId })
    if (res.ok) setTasks(prev => sortPersonalTasks([res.task, ...prev]))
    return res
  }, [userId])

  const setDone = useCallback(async (id, done) => {
    const patch = { status: done ? 'done' : 'open', completed_at: done ? new Date().toISOString() : null, updated_at: new Date().toISOString() }
    const { data, error } = await supabase.from('personal_tasks').update(patch).eq('id', id).select(COLS)
    if (error) return { ok: false, error: error.message }
    if (!data?.length) return { ok: false, error: 'Nothing was changed.' }
    setTasks(prev => sortPersonalTasks(prev.map(t => (t.id === id ? data[0] : t))))
    return { ok: true }
  }, [])

  const remove = useCallback(async (id) => {
    const { data, error } = await supabase.from('personal_tasks').delete().eq('id', id).select('id')
    if (error) return { ok: false, error: error.message }
    if (!data?.length) return { ok: false, error: 'Nothing was removed.' }
    setTasks(prev => prev.filter(t => t.id !== id))
    return { ok: true }
  }, [])

  return { tasks, access, loading, available, add, setDone, remove, reload: load }
}
